import { Combobox, ComboboxProps, Option } from '@fluentui/react-components';
import * as React from 'react';
import { Classification, Project } from '../hooks/client/types';
import { useTranslation } from 'react-i18next';

export interface ProjectGroup {
  group: Pick<Classification, 'name' | 'id'>;
  projects: Project[];
}

export function sortProjects(projects: Project[] = []): Project[] {
  return projects.sort((a, b) => {
    if (!a.name) return 1;
    if (!b.name) return -1;
    return a.name.localeCompare(b.name);
  });
}

const SELECT_ALL_VALUE = 'select-all';

interface ProjectSelectorProps {
  projects: Project[];
  projectPluralName?: string;
  selectedProjects?: number[];
  onChange?: (projectIds: number[]) => void;
  disabled?: boolean;
}

const ProjectSelelctor = ({ projects, projectPluralName = 'Projects', onChange, selectedProjects = [], disabled = false }: ProjectSelectorProps) => {
  const [value, setValue] = React.useState(
    projects
      .filter((p) => selectedProjects.includes(p.id))
      .map((x) => x.name)
      .join(', '),
  );
  const { t } = useTranslation();

  let filteredProjects = sortProjects(projects).filter((p) => p.name.toLocaleLowerCase().includes(value.toLocaleLowerCase()));

  const onSelect: ComboboxProps['onOptionSelect'] = (_, data) => {
    if (data.optionValue === SELECT_ALL_VALUE) {
      if (data.selectedOptions.includes(SELECT_ALL_VALUE)) {
        onChange?.(projects.map(({ id }) => id));
      } else {
        onChange?.([]);
      }
    } else {
      onChange?.(data.selectedOptions.filter((x) => x !== SELECT_ALL_VALUE).map((id) => parseInt(id)));
    }
  };

  const onOpenChange: ComboboxProps['onOpenChange'] = (_, data) => {
    if (!data.open) {
      setValue(
        projects
          .filter((p) => selectedProjects.includes(p.id))
          .map((x) => x.name)
          .join(', '),
      );
    } else {
      setValue('');
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const selectedItems = selectedProjects.map((x) => x.toString());
  if (projects.map((x) => x.id).filter((x) => !selectedProjects.includes(x)).length === 0) {
    selectedItems.push(SELECT_ALL_VALUE);
  }

  return (
    <Combobox
      disabled={disabled}
      autoComplete="off"
      aria-autocomplete="none"
      value={value}
      onChange={handleChange}
      onOptionSelect={onSelect}
      onOpenChange={onOpenChange}
      multiselect
      positioning={{ autoSize: false }}
      listbox={{ style: { maxHeight: '40vh' } }}
      placeholder={t('PROJECT_SELECTOR.PLACEHOLDER', { projectPluralName })}
      selectedOptions={selectedItems}
      data-testid="project-selector"
    >
      {filteredProjects.length === projects.length && <Option value={SELECT_ALL_VALUE}>{t('PROJECT_SELECTOR.SELECT_ALL')}</Option>}
      {filteredProjects.length === 0 && <div>{t('PROJECT_SELECTOR.NO_RESULTS')}</div>}
      {filteredProjects.map((p) => {
        return (
          <Option
            key={p.id}
            value={p.id.toString()}
          >
            {p.name}
          </Option>
        );
      })}
    </Combobox>
  );
};

export default ProjectSelelctor;
